import React, { useEffect, useContext } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { palette } from '../../../assets/styles/themes/tokens';
import { routes } from '../../../constants';
import { useSelector } from 'react-redux';
import useRouter from '../../../utils/useRouter';
import {
  selectUser,
  selectTeachersList,
  selectTeachersListSize,
  selectSelectedSchoolId
} from '../../../reducers/user.selectors';
import useAction from '../../../utils/useAction';
import { getSchoolSeats, getSchoolTeachers } from '../../../actions/user.actions';
import TeachersList from './SchoolTeachersList/TeachersList';
import AddSchoolTeacher from './AddSchoolTeacher';
import { useTranslation } from 'react-i18next';
import { SnackbarContext } from '../../../state/SnackbarContextProvider';

const ROWS_PER_PAGE = 20;

const SchoolTeachers: React.FC = () => {
  const [page, setPage] = React.useState(0);
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
  const { router } = useRouter();
  const user = useSelector(selectUser);
  const teachersList = useSelector(selectTeachersList) || [];
  const teachersTotal = useSelector(selectTeachersListSize) || 0;
  const schoolSeats = useSelector((state: any) => state.user.schoolSeats);
  const getSchoolTeachersAction = useAction(getSchoolTeachers);
  const getSchoolSeatsAction = useAction(getSchoolSeats);
  const { t } = useTranslation();
  const { openSnackbar } = useContext(SnackbarContext);
  const selectedSchoolId = useSelector(selectSelectedSchoolId);
  const [loading, setLoading] = React.useState(true);

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (user && !user.isSchoolAdmin) {
      router.push(`/${routes.dashboard.root}`);
    } else {
      if (!selectedSchoolId) return;
      
      getSchoolSeatsAction(selectedSchoolId).then((data: any) => {
        if (data.type !== 'GET_SCHOOL_SEATS_SUCCESS') {
          openSnackbar({
            type: 'error',
            message: t('Error while fetching school seats')
          });
        }
      });
      setLoading(true);
      getSchoolTeachersAction(selectedSchoolId, page + 1, ROWS_PER_PAGE, debouncedSearchQuery)
        .then((data: any) => {
          if (data.type !== 'GET_SCHOOL_TEACHERS_SUCCESS') {
            openSnackbar({
              type: 'error',
              message: t('Error while fetching teachers')
            });
          }
        })
        .finally(() => setLoading(false));
    }
  }, [user?.isSchoolAdmin, page, debouncedSearchQuery, selectedSchoolId]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setPage(0); // Reset to first page on new search
    setSearchQuery(query);
  };

  const handleRefreshTeachers = () => {
    if (!selectedSchoolId) return;
    getSchoolTeachersAction(selectedSchoolId, page + 1, ROWS_PER_PAGE, debouncedSearchQuery);
  };

  const handleTeacherAdded = () => {
    if (!selectedSchoolId) return;

    setShowAddForm(false);
    getSchoolTeachersAction(selectedSchoolId, page + 1, ROWS_PER_PAGE, debouncedSearchQuery, true);
  };

  let showChangeUserTypeButton: boolean = true;
  if (
    schoolSeats?.bibliothekSeats?.remaining <= 0 &&
    schoolSeats?.courseSeats?.remaining <= 0 &&
    schoolSeats?.kombiSeats?.remaining <= 0
  ) {
    showChangeUserTypeButton = false;
  }

  return (
    <Box className='root' paddingTop='10px' sx={{ minHeight: '100vh', backgroundColor: 'rgb(249, 250, 251)' }}>
      <Box className='container'>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography
            variant='h5'
            sx={{ color: palette.text.primary }}
            marginBottom='10px'
            marginTop='10px'
          >
            {t('Teachers')}
          </Typography>
        </Box>
        <Box marginBottom={2}>
          <TextField
            fullWidth
            size='small'
            placeholder={t('Search teachers...')}
            variant='outlined'
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </Box>
        <Box marginBottom={1} display='flex' justifyContent='flex-end'>
          <Button
            variant='outlined'
            startIcon={<AddCircleIcon />}
            onClick={() => setShowAddForm(true)}
          >
            {t('Add Teacher')}
          </Button>
        </Box>
        <AddSchoolTeacher
          onTeacherAdded={handleTeacherAdded}
          onClose={() => setShowAddForm(false) }
          open={showAddForm}
          schoolSeats={schoolSeats}
        />
        <TeachersList
          teachersList={teachersList}
          loading={loading}
          page={page}
          rowsPerPage={ROWS_PER_PAGE}
          onPageChange={handleChangePage}
          onRefreshTeachers={handleRefreshTeachers}
          showChangeUserTypeButton={showChangeUserTypeButton}
          schoolSeats={schoolSeats}
          totalCount={teachersTotal}
        />
      </Box>
    </Box>
  );
};

export default SchoolTeachers;

